import { useState, useEffect, useRef } from "react";
import {
  BlockStack,
  TextField,
  Button,
  InlineStack,
  Text,
  Popover,
  ActionList,
} from "@medentic/polaris-react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { LanguageTranslateIcon, MagicIcon } from "@medentic/polaris-icons";
import GenerateModal from "./GenerateModal";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@app/store/store";
import useLanguages from "@shared/hooks/common/useLanguages";
import { MultilingualText } from "@features/events/types";

interface GenerativeTextFieldProps {
  id: string;
  placementId: string;
  value: MultilingualText;
  onChange: (value: MultilingualText) => void;
  type?: "textField" | "textArea";
  placeholder?: MultilingualText;
  label: string;
  description?: string;
  canGenerate?: boolean;
  requiredIndicator?: boolean;
  error?: string;
  onValidationChange?: (isValid: boolean) => void;
  validateAllLanguages?: boolean;
}

interface LocalizeRequest {
  translate: Array<{
    id: string;
    from: string;
    to: string;
    text: string;
    placementId: string;
  }>;
}

interface LocalizeResponse {
  id: string;
  from: string;
  to: string;
  inputText: string;
  translatedText: string;
  placementId: string;
}

export default function GenerativeTextField({
  id,
  placementId,
  value,
  onChange,
  type = "textField",
  placeholder,
  label,
  description,
  canGenerate = true,
  requiredIndicator = false,
  error,
  onValidationChange,
  validateAllLanguages = false,
}: GenerativeTextFieldProps) {
  const { t } = useTranslation();

  const [isLocalizing, setIsLocalizing] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isTranslatePopoverOpen, setIsTranslatePopoverOpen] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const { languages = [] } = useLanguages();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );
  const latestValueRef = useRef(value);
  const filledLanguages = selectedLanguages.filter(
    (language) => value[language]?.trim().length > 0
  );
  const isSourceSelectionRequired = filledLanguages.length > 1;
  const autoSourceLanguage =
    filledLanguages.length === 1 ? filledLanguages[0] : "";
  const targetLanguages = autoSourceLanguage
    ? selectedLanguages.filter((language) => language !== autoSourceLanguage)
    : [];
  const canOpenSourceSelector = isSourceSelectionRequired;
  const canTranslateDirectly =
    !!autoSourceLanguage && targetLanguages.length > 0;
  const canTranslate = canOpenSourceSelector || canTranslateDirectly;
  const { POST } = useApiRequest();
  const translationDirectionLabel =
    canTranslateDirectly
      ? `${autoSourceLanguage} → ${targetLanguages.join(", ")}`
      : "";
  const translationActions = filledLanguages.map((language) => ({
    content: `${language} → ${selectedLanguages
      .filter((locale) => locale !== language)
      .join(", ")}`,
    onAction: () => void handleLocalize(language),
  }));

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!isSourceSelectionRequired) {
      setIsTranslatePopoverOpen(false);
    }
  }, [isSourceSelectionRequired]);

  // Validate and notify parent component whenever value or selectedLanguages changes
  useEffect(() => {
    if (!validateAllLanguages || selectedLanguages.length === 0) return;

    const allEmpty = selectedLanguages.every(
      (lang) => !value[lang]?.trim()
    );
    const allFilled = selectedLanguages.every(
      (lang) => value[lang]?.trim().length > 0
    );
    
    if (onValidationChange) {
      if (requiredIndicator) {
        // Required field: must have all languages filled
        onValidationChange(allFilled);
      } else {
        // Optional field: valid if all empty OR all filled
        onValidationChange(allEmpty || allFilled);
      }
    }

    // Check if at least one language is filled but not all (partial fill error)
    const someFieldsFilled = selectedLanguages.some((lang) => value[lang]?.trim().length > 0);

    if (someFieldsFilled && !allFilled) {
      setValidationError(t("component.generative_text.error.fill_all_languages"));
    } else {
      setValidationError("");
    }
  }, [value, selectedLanguages, validateAllLanguages, onValidationChange, requiredIndicator, t]);

  const handleGenerateClick = () => {
    setIsGenerateModalOpen(true);
  };

  const handleGenerateKeep = (text: string, targetLanguage: string) => {
    // Remove quotes if the text is wrapped in quotes
    const cleanText = text.replace(/^["']|["']$/g, "");
    onChange({ ...value, [targetLanguage]: cleanText });
    setIsGenerateModalOpen(false);
  };

  const handleLocalize = async (sourceLanguage: string) => {
    const sourceText = latestValueRef.current[sourceLanguage]?.trim();
    if (!sourceText) return;
    const currentTargetLanguages = selectedLanguages.filter(
      (language) => language !== sourceLanguage
    );
    if (currentTargetLanguages.length === 0) return;

    setIsLocalizing(true);
    setIsTranslatePopoverOpen(false);
    try {
      const allowedTargetLanguageSet = new Set(currentTargetLanguages);
      const translationRequest: LocalizeRequest = {
        translate: currentTargetLanguages.map((targetLanguage) => ({
          id: `${id}-${sourceLanguage}-to-${targetLanguage}`,
          from: sourceLanguage,
          to: targetLanguage,
          text: sourceText,
          placementId: placementId,
        })),
      };

      const response = await POST<LocalizeResponse[]>(
        "/api/languages/translate-with-context",
        translationRequest
      );

      if (Array.isArray(response) && response.length > 0) {
        const translatedValues = response.reduce<MultilingualText>(
          (acc, result) => {
            if (
              result?.to &&
              allowedTargetLanguageSet.has(result.to) &&
              typeof result.translatedText === "string"
            ) {
              acc[result.to] = result.translatedText;
            }
            return acc;
          },
          {}
        );

        if (Object.keys(translatedValues).length > 0) {
          onChange({ ...latestValueRef.current, ...translatedValues });
        }
      }
    } catch (error) {
      console.error("Localization failed:", error);
    } finally {
      setIsLocalizing(false);
    }
  };

  const fieldProps = {
    label: "",
    autoComplete: "off" as const,
    ...(type === "textArea" && {
      multiline: 5,
      maxHeight: "150px", // Restricts height to approximately 3 rows
    }),
  };

  return selectedLanguages.length > 0 ? (
    <BlockStack gap="100">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="h3" variant="bodyMd" requiredIndicator={requiredIndicator}>
          {label}
        </Text>
        <InlineStack gap="200">
          {canGenerate && (
            <Button
              size="micro"
              variant="tertiary"
              onClick={handleGenerateClick}
              disabled={isLocalizing}
              icon={MagicIcon}
            >
              {t("component.generative_text.genetate_cta")}
            </Button>
          )}
          <Popover
            active={isSourceSelectionRequired && isTranslatePopoverOpen}
            activator={
              <Button
                size="micro"
                variant="tertiary"
                onClick={() => {
                  if (!canTranslate || isLocalizing) return;
                  if (isSourceSelectionRequired) {
                    setIsTranslatePopoverOpen((active) => !active);
                    return;
                  }
                  void handleLocalize(autoSourceLanguage);
                }}
                loading={isLocalizing}
                disabled={!canTranslate}
                icon={LanguageTranslateIcon}
                disclosure={isSourceSelectionRequired}
              >
                {translationDirectionLabel
                  ? `${t("component.generative_text.translate_cta")} ${translationDirectionLabel}`
                  : t("component.generative_text.translate_cta")}
              </Button>
            }
            autofocusTarget="first-node"
            onClose={() => setIsTranslatePopoverOpen(false)}
          >
            <ActionList
              actionRole="menuitem"
              items={translationActions}
            />
          </Popover>
        </InlineStack>
      </InlineStack>
      <BlockStack gap="200">
        {[...selectedLanguages].sort().map((language, index) => {
          const hasError = error && !value[language]?.trim();
          const hasValidationError = validationError && !value[language]?.trim();
          const displayError = hasError || hasValidationError;
          
          return (
            <TextField
              key={language}
              {...fieldProps}
              value={value[language] || ""}
              onChange={(newValue) =>
                onChange({ ...value, [language]: newValue })
              }
              placeholder={placeholder?.[language] || ""}
              prefix={
                <img
                  src={languages.find((l) => l.iso === language)?.svg}
                  alt={language}
                  style={{ display: "flex", width: "16px" }}
                />
              }
              error={
                index === selectedLanguages.length - 1 && (error || validationError)
                  ? error || validationError // Show error message only on last field
                  : displayError
                  ? true // Show red border only for empty fields when there's an error
                  : undefined
              }
            />
          );
        })}
      </BlockStack>
      {description && (
        <Text as="p" variant="bodyMd" tone="subdued">
          {description}
        </Text>
      )}
      <GenerateModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onKeep={handleGenerateKeep}
        placementId={placementId}
        id={id}
        label={label}
        supportedLanguages={languages
          .filter((l) => l.supported)
          .map((l) => ({
            label: l.localLabel,
            value: l.iso,
          }))}
        selectedLanguages={selectedLanguages}
        value={value}
      />
    </BlockStack>
  ) : null;
}
